<script setup lang="ts">
  import { computed } from 'vue';
  import {
    MpfCheckboxItem,
    MpfCheckboxOptionItem,
    MpfCheckboxesProps,
    MpfCheckboxesEmits,
    mpfCheckboxesDefaults,
  } from './MpfCheckboxes.props';
  import MpfLabel from '../MpfLabel/MpfLabel.vue';
  import MpfHint from '../MpfHint/MpfHint.vue';
  defineOptions({ name: 'MpfCheckboxes' });

  const props = withDefaults(defineProps<MpfCheckboxesProps>(), mpfCheckboxesDefaults);
  const emit = defineEmits<MpfCheckboxesEmits>();

  const baseId = computed(() => props.id || props.name || 'mpf-checkboxes');
  const inputName = computed(() => props.name || baseId.value);
  const hintId = computed(() => (props.hint ? `${baseId.value}-hint` : undefined));
  const errorId = computed(() => (props.errors ? `${baseId.value}-errors` : undefined));

  const headingTag = computed(() =>
    props.legendHeadingLevel
      ? (`h${props.legendHeadingLevel}` as keyof HTMLElementTagNameMap)
      : 'div',
  );
  const headingSize = computed(() =>
    props.legendHeadingSize ? `mpf-fieldset__legend--${props.legendHeadingSize}` : null,
  );

  const getItemId = (item: MpfCheckboxOptionItem, index: number) =>
    item.id || `${baseId.value}-${index}`;

  const getItemKey = (item: MpfCheckboxItem, index: number) =>
    item.type === 'divider'
      ? `divider-${index}-${item.text}`
      : `checkbox-${getItemId(item, index)}`;

  const describedBy = () => {
    const ids: string[] = [];
    if (hintId.value) ids.push(hintId.value);
    if (errorId.value) ids.push(errorId.value);
    if (props.ariaDescribedby) ids.push(props.ariaDescribedby);
    return ids.length ? ids.join(' ') : undefined;
  };

  const onChange = (value: string | number | null, event: Event) => {
    if (props.disabled) return;

    const current = Array.isArray(props.modelValue) ? [...props.modelValue] : [];

    const index = current.indexOf(value);

    if (index === -1) {
      current.push(value);
    } else {
      current.splice(index, 1);
    }

    emit('update:modelValue', current);
    emit('change', current, event);
  };

  const onBlur = (event: FocusEvent) => emit('blur', event);
</script>
<template>
  <div class="mpf-form-group mpf-checkboxes" :class="{ 'mpf-form-group--error': !!errors.length }">
    <fieldset class="mpf-fieldset">
      <legend v-if="label" class="mpf-fieldset__legend" :class="headingSize">
        <component :is="headingTag" class="mpf-fieldset__heading">
          {{ label }}
        </component>
      </legend>

      <MpfHint v-if="hint" :hint="hint" :sr-hint="srHint" :id="hintId" />

      <span v-if="errors.length" class="mpf-error-message" :id="errorId">
        <span v-if="errors[0]" class="mpf-u-visually-hidden">Error:</span>
        {{ errors![0] }}
      </span>

      <div class="mpf-checkboxes">
        <template v-for="(item, index) in items" :key="getItemKey(item, index)">
          <div v-if="item.type === 'divider'" class="mpf-checkboxes__divider">
            {{ item.text }}
          </div>

          <div v-else class="mpf-checkboxes__item">
            <input
              class="mpf-checkboxes__input"
              type="checkbox"
              :id="getItemId(item, index)"
              :name="inputName"
              :value="item.value"
              :checked="Array.isArray(modelValue) && modelValue.includes(item.value)"
              :disabled="disabled || !!item.disabled"
              :aria-describedby="describedBy()"
              @change="onChange(item.value, $event)"
              @blur="onBlur"
            />

            <mpf-label
              class="mpf-label mpf-checkboxes__label"
              :label="item.label"
              :for="getItemId(item, index)"
              data-private
            />
            <MpfHint
              v-if="item.hint"
              class="mpf-checkboxes__hint"
              :hint="item.hint"
              :sr-hint="item.srHint"
              :id="getItemId(item, index)"
            />
            <MpfHint
              v-if="item.hintSecond"
              class="mpf-checkboxes__hint--second"
              :hint="item.hintSecond"
              :sr-hint="item.hintSecond"
              :id="getItemId(item, index)"
            />
          </div>
        </template>
      </div>
    </fieldset>
  </div>
</template>

<style lang="scss" scoped>
  @use './MpfCheckboxes.scss';
</style>
